import {Box, Typography} from '@mui/material';
import React, {useMemo} from 'react';
import {VictoryArea, VictoryChart, VictoryPolarAxis} from 'victory';
import {useTranslation} from 'react-i18next';
import {SKY_BLUE, VIVID_VIOLET} from '../../../styles';
import {PositionGroup} from '../../../enums';
import GraphLegendItem from '../GraphLegendItem';
import {getMean} from '../../../services/mathService';

interface Props {
  positionGroup: PositionGroup;
  practiceEfficiencyMax: number;
  data: {x: string; y: number}[];
}

const EfficiencyPolarChart: React.FC<Props> = ({
  data,
  practiceEfficiencyMax,
  positionGroup,
}) => {
  const {t} = useTranslation();
  const isForwards = useMemo(
    () => positionGroup === PositionGroup.forwards,
    [positionGroup],
  );

  const mean = useMemo(
    () => getMean(data.filter(d => d.y > 0).map(d => d.y)),
    [data],
  );

  // Duplicate single data point to make VictoryArea render properly
  const chartData = useMemo(() => {
    if ((data ?? []).length === 1) {
      return [
        ...data,
        {
          x: ' ',
          y: 0,
        },
      ];
    }
    return data;
  }, [data]);

  return (
    <Box sx={{mb: 2}}>
      <Box sx={{mb: 2}}>
        <VictoryChart
          domain={{y: [0, practiceEfficiencyMax]}}
          padding={{left: 20, right: 20, top: 20, bottom: 20}}
          height={300}
          polar>
          {(chartData ?? []).length > 0 && (
            <VictoryPolarAxis
              tickFormat={t => `${t}`}
              style={{
                axisLabel: {padding: 10},
                ticks: {stroke: '#fff', size: 5},
                tickLabels: {fontSize: 12, fill: '#fff', angle: 0},
                grid: {stroke: '#fff', strokeWidth: 0.25, opacity: 0.5},
              }}
            />
          )}
          <VictoryPolarAxis
            tickFormat={t => `${t}%`}
            axisAngle={90}
            dependentAxis
            style={{
              axisLabel: {padding: 30},
              tickLabels: {
                fontSize: 10,
                fill: '#fff',
                angle: 0,
                textAnchor: 'end',
              },
              grid: {stroke: '#fff', strokeWidth: 0.25, opacity: 0.5},
            }}
          />
          {(chartData ?? []).length > 0 && (
            <VictoryArea
              data={chartData}
              style={{
                data: {
                  fill: isForwards ? SKY_BLUE : VIVID_VIOLET,
                  stroke: isForwards ? SKY_BLUE : VIVID_VIOLET,
                  fillOpacity: 0.2,
                },
              }}
            />
          )}
        </VictoryChart>
      </Box>
      <GraphLegendItem
        label={isForwards ? t('position.forwards') : t('position.defense')}
        borderColor={isForwards ? SKY_BLUE : VIVID_VIOLET}
        backgroundColor={isForwards ? `${SKY_BLUE}33` : `${VIVID_VIOLET}33`}
        sx={{mb: 2}}
      />
      {mean && (
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
          }}>{`${t('general.average')}: ${mean.toFixed(1)}%`}</Typography>
      )}
    </Box>
  );
};

export default EfficiencyPolarChart;
